import { uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import Asset from '../model/asset';
import RegisteredAction from '../model/registered-action';
import RegisteredActionType from '../model/registered-action-type';
import { User } from '../model/user';
import { CollectionHelper } from './collection-helper';
import { StringHelper } from './string-helper';

type ID = { id?: number };

export class GeneralHelper {
  static readonly hours = ['0', '1', '2', '3', '4', '5', '6', '7', '8'];

  static readonly minutes = Array(60)
    .fill(1)
    .map((x, y) => String(x + y - 1).padStart(2, '0'));

  static readonly noop = () => {};

  static compareEntities(o1, o2) {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.id === o2.id;
    } else {
      return false;
    }
  }

  // intended for use with e.g. mat-select where o1 is a value from an option and o2 is a value from the selection
  // if o1.id == null, this should represent that no value was selected
  static compareEntitiesSupportingNull(o1: ID, o2: ID): boolean {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1.id == null && o2 == null) {
      return true;
    } else if (o1.id == null && o2 != null && o2.id == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.id === o2.id;
    } else {
      return false;
    }
  }

  static ordinalEnumGetter(o: any): string {
    return o.label;
  }

  static compareMaterial(o1: any, o2: any): boolean {
    return (
      (o1 == null && o2 == null) ||
      (o1 != null &&
        o2 != null &&
        o1.name === o2.name &&
        o1.ordinal === o2.ordinal)
    );
  }

  static compareOrdinalEnum(o1: any, o2: any): boolean {
    return (
      (o1 == null && o2 == null) ||
      (o1 != null &&
        o2 != null &&
        o1.label === o2.label &&
        o1.ordinal === o2.ordinal)
    );
  }

  static compareValueEnum(o1: any, o2: any): boolean {
    return (
      (o1 == null && o2 == null) ||
      (o1 != null && o2 != null && o1.value === o2.value)
    );
  }

  static compareNameEnum(o1: any, o2: any): boolean {
    return (
      (o1 == null && o2 == null) ||
      (o1 != null && o2 != null && o1.name === o2.name)
    );
  }

  static compareNull(v1: any, v2: any, nullFirst = true): number {
    if ((v1 == null) == (v2 == null)) {
      return 0;
    }
    if (v1 == null) {
      return nullFirst ? -1 : 1;
    }
    return nullFirst ? 1 : -1; // v2 == null
  }

  // return true <=> all of the given objects are null or all of the given objects are not null
  static equivalentNullState(...arr: any): boolean {
    return uniqBy(arr, (e: any) => e == null).length <= 1;
  }

  static copy(object: any, source: any): void {
    Object.keys(source).forEach(function (key) {
      object[key] = source[key];
    });
  }

  static trackById(index: number, obj: any): number {
    return obj.id;
  }
  static hasOwnProperty(obj: any, prop: string): boolean {
    if (obj == null) {
      return false;
    }
    return Object.prototype.hasOwnProperty.call(obj, prop);
  }

  // For debugging purposes
  static mapToJSON(map: any): string {
    return map == null
      ? JSON.stringify(null)
      : JSON.stringify(Array.from(map.entries()));
  }

  static downloadAsset(asset: Asset): void {
    const link = document.createElement('a');
    link.href = '/api/assets/generated/' + asset.uuid;
    link.setAttribute('download', '');
    link.click();
  }

  static initializeRegisteredAction(
    user: User,
    date?: Date,
    type?: RegisteredActionType,
    offline: boolean = false
  ) {
    const toReturn = new RegisteredAction();
    toReturn.date = date == null ? new Date() : date;
    toReturn.actualDate =
      toReturn.date == null
        ? null
        : moment.tz(toReturn.date, 'Europe/Brussels');
    toReturn.user = new User();
    toReturn.user.id = user.id;
    toReturn.user.firstName = user.firstName;
    toReturn.user.lastName = user.lastName;
    if (type != null) {
      toReturn.type = type;
    }
    if (offline) {
      toReturn.localUuid = uuidv4();
    }
    return toReturn;
  }

  static compareRegisteredActions(
    r1: RegisteredAction,
    r2: RegisteredAction
  ): number {
    if (r1 == null && r2 == null) {
      return 0;
    }
    if (r1 == null) {
      return -1;
    }
    if (r2 == null) {
      return 1;
    }
    if (r1.date == null && r2.date == null) {
      return 0;
    }
    if (r1.date == null) {
      return -1;
    }
    if (r2.date == null) {
      return 1;
    }
    if (r1.actualDate == null) {
      r1.actualDate = moment.tz(r1.date, 'Europe/Brussels');
    }
    if (r2.actualDate == null) {
      r2.actualDate = moment.tz(r2.date, 'Europe/Brussels');
    }
    return r1.actualDate.diff(r2.actualDate);
  }

  static compareRegisteredActionsDesc(
    r1: RegisteredAction,
    r2: RegisteredAction
  ): number {
    return -GeneralHelper.compareRegisteredActions(r1, r2);
  }

  static getMinutesInHourAndMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const outMinutes = minutes % 60;
    return StringHelper.join(
      [hours > 0 ? `${hours}u` : null, `${outMinutes}m`],
      ' '
    );
  }

  /**
   * minutes == null => return == null;
   * return[0]: hours
   * return[1]: minutes
   */
  static getMinutesInHourAndMinutesArray(minutes: number): number[] {
    return minutes == null ? null : [Math.floor(minutes / 60), minutes % 60];
  }

  static getTotalExecutionMinutes(registeredActions: RegisteredAction[]) {
    if (!CollectionHelper.isPopulated(registeredActions)) {
      return 0;
    }
    // only count start actions that are eventually followed by a stop action without another start action intervening
    let sum = 0;
    let currentAction = null;
    for (let i = registeredActions.length - 1; i >= 0; i--) {
      const action = registeredActions[i];
      if (action.type != null) {
        if (action.type.ordinal === 0) {
          currentAction = action;
        } else if (action.type.ordinal === 1 && currentAction != null) {
          sum += moment(action.date).diff(currentAction.date, 'minutes');
          currentAction = null;
        }
      }
    }
    return sum;
  }

  static getScrollTop(el: any = window): number {
    return el.pageYOffset !== undefined ? el.pageYOffset : el.scrollTop;
  }

  static isNumeric(i: any): boolean {
    return !isNaN(i);
  }

  static falseIfNull(b: boolean): boolean {
    return b == null ? false : b;
  }

  static identity<T>(o: T): T {
    return o;
  }

  static invertNullableBoolean(b?: boolean): boolean {
    return b == null ? true : !b;
  }

  static addGuardNull(a: number, b: number): number {
    if (a == null && b == null) {
      return null;
    }
    if (a == null) {
      return b;
    }
    if (b == null) {
      return a;
    }
    return a + b;
  }

  static sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), ms);
    });
  }
}
